import React, { useContext, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  ArrowRight, ArrowLeft, Volume2, CheckCircle, XCircle, 
  Loader2, Mic, MicOff, Play, RotateCcw, Trophy, Star, ChevronDown, ChevronUp, Lightbulb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fill Blank Exercise Component - Improved with collapsible hint and responsive buttons
function FillBlankExercise({ exercise, showResult, selectedOption, handleAnswer }) {
  const [showHint, setShowHint] = useState(false);
  
  return (
    <div>
      <p className="text-lg font-bold text-gray-800 mb-4 text-center">
        {exercise.instruction || "Boşluğu doldurun"}
      </p>
      
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <p className="text-xl text-gray-800 text-center leading-relaxed">
          {exercise.content?.sentence?.replace('___', '_______')}
        </p>
        
        {/* Collapsible Hint */}
        {exercise.content?.hint && (
          <div className="mt-4">
            <button
              onClick={() => setShowHint(!showHint)}
              className="flex items-center justify-center gap-2 mx-auto text-sm text-amber-600 hover:text-amber-700 transition-colors"
            >
              <Lightbulb className="w-4 h-4" />
              <span>{showHint ? 'İpucunu Gizle' : 'İpucu Göster'}</span>
              {showHint ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHint && (
              <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 animate-in slide-in-from-top-2">
                <p className="text-sm text-amber-700 text-center">
                  💡 {exercise.content.hint}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Responsive grid - 1 column on mobile, 2 on larger screens */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {exercise.content?.options?.map((option, idx) => (
          <Button
            key={idx}
            variant="outline"
            onClick={() => handleAnswer(option, idx)}
            disabled={showResult}
            className={`min-h-[56px] h-auto py-3 px-4 text-base font-medium border-2 transition-all whitespace-normal text-center leading-snug ${
              showResult && idx === exercise.correct_answer
                ? 'bg-green-100 border-green-500 text-green-700'
                : showResult && selectedOption === idx && idx !== exercise.correct_answer
                ? 'bg-red-100 border-red-500 text-red-700'
                : 'hover:bg-sky-50 hover:border-sky-300'
            }`}
          >
            {option}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default function CabinCrewComprehensive() {
  const { level, moduleId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial stage from URL or default to 1
  const initialStage = parseInt(searchParams.get('stage')) || 1;
  
  // State
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentStage, setCurrentStage] = useState(initialStage);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [stageScores, setStageScores] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [stageComplete, setStageComplete] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  
  const audioRef = useRef(null);

  // Load lesson for current stage
  const loadStage = async (stage) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/cabin-crew/comprehensive-lesson/${level}/${moduleId}?stage=${stage}`
      );
      setLesson(response.data);
      setSessionId(response.data.session_id);
      setCurrentQuestion(0);
      setAnswers({});
      setScore(0);
      setShowResult(false);
      setStageComplete(false);
    } catch (error) {
      console.error('Stage load error:', error);
      const errorMessage = error.response?.data?.detail || 'Aşama yüklenemedi';
      toast.error(errorMessage);
      
      // If invalid module, redirect to modules page
      if (error.response?.status === 400 && errorMessage.includes('Invalid module')) {
        toast.error('Geçersiz modül. Modül listesine yönlendiriliyorsunuz.');
        setTimeout(() => navigate('/cabin-crew/modules'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && level && moduleId) {
      loadStage(currentStage);
    }
  }, [user, level, moduleId, currentStage, navigate]);

  // Play audio
  const playAudio = async (text) => {
    if (!text) {
      console.warn('No text provided for audio');
      return;
    }
    
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        setAudioPlaying(true);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.onend = () => setAudioPlaying(false);
        utterance.onerror = () => setAudioPlaying(false);
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      setAudioPlaying(true);
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audioUrl = URL.createObjectURL(response.data);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('Audio API error:', error);
      toast.error('Ses çalınamadı');
    } finally {
      setAudioPlaying(false);
    }
  };

  // Handle answer
  const handleAnswer = async (answer, optionIndex = null) => {
    const exercise = lesson.exercises[currentQuestion];
    setSelectedOption(optionIndex);
    
    // Check answer based on exercise type
    if (exercise.type === 'fill_blank') {
      const correct = optionIndex === exercise.correct_answer;
      setIsCorrect(correct);
      setShowResult(true);
      
      if (correct) {
        setScore(prev => prev + 1);
        toast.success('Doğru! 🎉');
      } else {
        toast.error('Yanlış!');
      }
      
      setAnswers(prev => ({ ...prev, [currentQuestion]: { answer: optionIndex, correct } }));
      
      // Auto advance
      setTimeout(() => nextQuestion(), 1500);
      
    } else if (exercise.type === 'scenario') {
      const selectedOpt = exercise.content?.options?.[optionIndex];
      const correct = selectedOpt?.is_correct === true;
      setIsCorrect(correct);
      setShowResult(true);
      
      if (correct) {
        setScore(prev => prev + 1);
        toast.success('Doğru! 🎉');
      } else {
        toast.error('Yanlış!');
      }
      
      setAnswers(prev => ({ ...prev, [currentQuestion]: { answer: optionIndex, correct } }));
      setTimeout(() => nextQuestion(), 1500);
      
    } else {
      // For vocabulary, dialogue, etc - mark as viewed
      setAnswers(prev => ({ ...prev, [currentQuestion]: { answer: 'viewed', correct: true } }));
      setScore(prev => prev + 1);
      nextQuestion();
    }
  };

  // Next question
  const nextQuestion = () => {
    setShowResult(false);
    setSelectedOption(null);
    setIsCorrect(null);
    
    if (currentQuestion < lesson.exercises.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Stage complete
      completeStage();
    }
  };

  // Complete stage
  const completeStage = async () => {
    const stageScore = Math.round((score / lesson.exercises.length) * 100);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/cabin-crew/complete-stage?session_id=${sessionId}&stage=${currentStage}&score=${stageScore}`
      );
      
      setStageScores(prev => ({ ...prev, [currentStage]: stageScore }));
      setStageComplete(true);
      
      if (response.data.is_fully_completed) {
        setAllComplete(true);
        toast.success(`🎉 Tüm aşamalar tamamlandı! +${response.data.miles_earned} Mil`);
      } else {
        toast.success(`Aşama ${currentStage} tamamlandı! ${stageScore}%`);
      }
    } catch (error) {
      console.error('Complete stage error:', error);
      setStageComplete(true);
    }
  };

  // Go to next stage
  const goToNextStage = () => {
    if (currentStage < 8) {
      const nextStage = currentStage + 1;
      setCurrentStage(nextStage);
      // Update URL with new stage
      setSearchParams({ stage: nextStage.toString() });
      // useEffect will automatically load the new stage
    }
  };

  // Render exercise content
  const renderExercise = () => {
    if (!lesson || !lesson.exercises || currentQuestion >= lesson.exercises.length) {
      return <div className="text-center text-gray-500">Egzersiz yükleniyor...</div>;
    }

    const exercise = lesson.exercises[currentQuestion];
    
    switch (exercise.type) {
      case 'vocabulary':
        return (
          <div className="text-center">
            <div 
              className="mb-6 cursor-pointer group"
              onClick={() => playAudio(exercise.content?.word)}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-sky-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-10 h-10 text-white" />
              </div>
              <p className="text-4xl font-black text-gray-800 mb-2">
                {exercise.content?.word}
              </p>
              {exercise.content?.pronunciation && (
                <p className="text-lg text-gray-500 italic">
                  {exercise.content.pronunciation}
                </p>
              )}
            </div>
            
            <div className="bg-sky-50 rounded-xl p-4 mb-4">
              <p className="text-2xl font-bold text-sky-700">
                {exercise.content?.translation}
              </p>
            </div>
            
            {exercise.content?.example && (
              <div className="bg-gray-50 rounded-xl p-4 mt-4">
                <p className="text-lg text-gray-700 italic mb-1">
                  &quot;{exercise.content.example}&quot;
                </p>
                {exercise.content?.example_translation && (
                  <p className="text-sm text-gray-500">
                    {exercise.content.example_translation}
                  </p>
                )}
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Öğrendim, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'matching': {
        const content = exercise.content || {};
        let matchTerms = [];
        let matchMeanings = [];
        
        if (content.pairs) {
          if (Array.isArray(content.pairs)) {
            matchTerms = content.pairs.map(p => p.english || p.term || '');
            matchMeanings = content.pairs.map(p => p.turkish || p.match || '');
          } else {
            matchTerms = Object.keys(content.pairs);
            matchMeanings = Object.values(content.pairs);
          }
        } else if (content.terms) {
          matchTerms = content.terms;
          matchMeanings = content.translations || content.meanings || [];
        }
        
        return (
          <div>
            <p className="text-lg font-bold text-gray-800 mb-4 text-center">
              {exercise.instruction || "İngilizce terimleri Türkçe anlamlarıyla eşleştirin"}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <p className="text-sm font-bold text-sky-600 mb-2 text-center">English</p>
                {matchTerms.map((term, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-sky-50 rounded-xl border-2 border-sky-200 font-medium text-gray-800 text-center"
                  >
                    {term}
                  </div>
                ))}
              </div>
              
              <div className="space-y-3">
                <p className="text-sm font-bold text-indigo-600 mb-2 text-center">Türkçe</p>
                {matchMeanings.map((meaning, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-indigo-50 rounded-xl border-2 border-indigo-200 font-medium text-gray-800 text-center"
                  >
                    {meaning}
                  </div>
                ))}
              </div>
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 w-full bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Eşleştirmeleri Gördüm, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'fill_blank':
        return (
          <FillBlankExercise 
            exercise={exercise}
            showResult={showResult}
            selectedOption={selectedOption}
            handleAnswer={handleAnswer}
          />
        );

      case 'dialogue':
        return (
          <div>
            <p className="text-lg font-bold text-gray-800 mb-4 text-center">
              {exercise.instruction || "Bu diyaloğu okuyun"}
            </p>
            
            {exercise.content?.context && (
              <div className="bg-amber-50 rounded-xl p-3 mb-4 text-center">
                <p className="text-sm text-amber-700">{exercise.content.context}</p>
              </div>
            )}
            
            <div className="space-y-3">
              {exercise.content?.lines?.map((line, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-xl ${
                    line.speaker === 'Crew' 
                      ? 'bg-sky-50 border-l-4 border-sky-400' 
                      : 'bg-gray-50 border-l-4 border-gray-300'
                  }`}
                >
                  <p className="text-xs font-bold text-gray-500 mb-1">{line.speaker}</p>
                  <p className="text-lg font-medium text-gray-800">{line.text}</p>
                  <p className="text-sm text-gray-500 italic">{line.translation}</p>
                </div>
              ))}
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 w-full bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Diyaloğu Anladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'scenario':
        return (
          <div>
            <div className="bg-amber-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-600 mb-1">Senaryo:</p>
              <p className="text-lg font-medium text-gray-800">{exercise.content?.situation}</p>
              <p className="text-sm text-gray-600 italic mt-1">{exercise.content?.situation_tr}</p>
            </div>
            
            <p className="text-lg font-bold text-gray-800 mb-4 text-center">
              {exercise.instruction || "Bu durumda ne dersiniz?"}
            </p>
            
            <div className="space-y-3">
              {exercise.content?.options?.map((option, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  onClick={() => handleAnswer(option.text, idx)}
                  disabled={showResult}
                  className={`w-full h-auto py-4 text-left justify-start border-2 ${
                    showResult && option.is_correct
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : showResult && selectedOption === idx && !option.is_correct
                      ? 'bg-red-100 border-red-500 text-red-700'
                      : 'hover:bg-sky-50 hover:border-sky-300'
                  }`}
                >
                  {option.text}
                </Button>
              ))}
            </div>
          </div>
        );

      case 'pronunciation': {
        const pronunciationWord = exercise.content?.word || exercise.content?.text || '';
        const phonetic = exercise.content?.phonetic || exercise.content?.pronunciation || '';
        
        return (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Bu kelimeyi/cümleyi telaffuz edin:</p>
            
            <div 
              className="mb-6 cursor-pointer group"
              onClick={() => playAudio(pronunciationWord)}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-12 h-12 text-white" />
              </div>
              <p className="text-3xl font-black text-gray-800 mb-2">
                {pronunciationWord}
              </p>
              {phonetic && (
                <p className="text-lg text-gray-500 italic">
                  {phonetic}
                </p>
              )}
            </div>
            
            {exercise.content?.translation && (
              <div className="bg-purple-50 rounded-xl p-4 mb-4">
                <p className="text-xl font-bold text-purple-700">
                  {exercise.content.translation}
                </p>
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-4 bg-gradient-to-r from-pink-500 to-purple-600"
            >
              Telaffuz Ettim, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'icao_spelling':
        return (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Bu kelimeyi ICAO alfabesiyle heceleyiniz:</p>
            <p className="text-4xl font-black text-gray-800 mb-6">
              {exercise.content?.word}
            </p>
            
            <div className="bg-sky-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-500 mb-2">Doğru heceleme:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {exercise.content?.correct_spelling?.map((word, idx) => (
                  <span key={idx} className="px-3 py-1 bg-sky-100 rounded-lg font-bold text-sky-700">
                    {word}
                  </span>
                )) || <span className="text-gray-500">Yükleniyor...</span>}
              </div>
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-4 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Anladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'pa_announcement': {
        const announcementText = exercise.content?.text || '';
        const tips = exercise.content?.tips || [];
        
        return (
          <div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-2xl">📢</span>
              <p className="text-lg font-bold text-gray-800">
                {exercise.content?.announcement_type || 'Genel'} Anonsu
              </p>
            </div>
            
            {/* Audio Play Button */}
            <div className="flex justify-center mb-4">
              <button
                onClick={() => playAudio(announcementText)}
                disabled={audioPlaying}
                className="w-16 h-16 bg-gradient-to-br from-sky-400 to-indigo-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-50"
              >
                {audioPlaying ? (
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                ) : (
                  <Volume2 className="w-8 h-8 text-white" />
                )}
              </button>
            </div>
            
            <div className="bg-gradient-to-br from-sky-50 to-indigo-50 rounded-xl p-6 mb-4">
              <p className="text-lg text-gray-800 mb-4 leading-relaxed">
                {announcementText}
              </p>
              {exercise.content?.translation && (
                <p className="text-sm text-gray-600 italic border-t pt-3">
                  {exercise.content.translation}
                </p>
              )}
            </div>
            
            {tips.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-amber-700 mb-2">💡 İpuçları:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {tips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="w-full mt-4 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Anladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'interview_question': {
        const interviewTips = exercise.content?.tips || [];
        const keyPoints = exercise.content?.key_points || [];
        
        return (
          <div>
            <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-6 mb-6 text-white">
              <p className="text-sm opacity-75 mb-2">Mülakat Sorusu ({exercise.content?.category || 'genel'})</p>
              <p className="text-xl font-bold mb-2">{exercise.content?.question}</p>
              <p className="text-sm opacity-90 italic">{exercise.content?.question_tr}</p>
            </div>
            
            {interviewTips.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-amber-700 mb-2">💡 Cevap İpuçları:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {interviewTips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {keyPoints.length > 0 && (
              <div className="bg-sky-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-sky-700 mb-2">🎯 Değinilmesi Gerekenler:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {keyPoints.map((point, idx) => (
                    <li key={idx}>✓ {point}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {exercise.content?.sample_answer && (
              <div className="bg-green-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-green-700 mb-2">✨ Örnek Cevap:</p>
                <p className="text-sm text-gray-700">{exercise.content.sample_answer}</p>
                {exercise.content?.sample_answer_tr && (
                  <p className="text-xs text-gray-500 mt-2 italic">{exercise.content.sample_answer_tr}</p>
                )}
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="w-full mt-4 bg-gradient-to-r from-purple-500 to-indigo-600"
            >
              Anladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'voice_response':
        return (
          <div className="text-center">
            <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl p-6 mb-6 text-white">
              <p className="text-sm opacity-75 mb-2">Sesli Cevap Verin</p>
              <p className="text-xl font-bold">{exercise.content?.prompt}</p>
              <p className="text-sm opacity-90 italic mt-2">{exercise.content?.prompt_tr}</p>
            </div>
            
            {exercise.content?.context && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm text-gray-600">{exercise.content.context}</p>
              </div>
            )}
            
            <div className="flex justify-center gap-4 mb-6">
              <Button
                onClick={() => setIsRecording(!isRecording)}
                className={`w-20 h-20 rounded-full ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-gradient-to-br from-pink-500 to-purple-600'
                }`}
              >
                {isRecording ? (
                  <MicOff className="w-10 h-10" />
                ) : (
                  <Mic className="w-10 h-10" />
                )}
              </Button>
            </div>
            
            {exercise.content?.sample_response && (
              <div className="bg-green-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm font-bold text-green-700 mb-2">✨ Örnek Cevap:</p>
                <p className="text-sm text-gray-700">{exercise.content.sample_response}</p>
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-600"
            >
              Cevapladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      default:
        return (
          <div className="text-center text-gray-500">
            <p>Egzersiz tipi: {exercise.type}</p>
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-4 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-sky-500 mx-auto mb-4" />
            <p className="text-gray-600">Aşama {currentStage} yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  // Stage complete screen
  if (stageComplete) {
    const stageScore = Math.round((score / (lesson?.exercises?.length || 1)) * 100);
    
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-4">
          <div className="max-w-lg mx-auto">
            <Card className="border-4 border-sky-300 shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-12 h-12 text-white" />
                </div>
                
                <h2 className="text-3xl font-black text-gray-800 mb-2">
                  {allComplete ? '🎉 Tebrikler!' : `Aşama ${currentStage} Tamamlandı!`}
                </h2>
                
                <p className="text-gray-600 mb-6">
                  {allComplete 
                    ? 'Tüm 8 aşamayı başarıyla tamamladınız!'
                    : `${currentStage}/8 aşama tamamlandı`
                  }
                </p>
                
                <div className="bg-sky-50 rounded-xl p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-4xl font-black text-sky-600">{score}/{lesson?.exercises?.length}</p>
                      <p className="text-sm text-gray-500">Doğru</p>
                    </div>
                    <div>
                      <p className="text-4xl font-black text-green-600">{stageScore}%</p>
                      <p className="text-sm text-gray-500">Başarı</p>
                    </div>
                  </div>
                </div>
                
                {/* Stage Progress */}
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">Aşama İlerlemesi</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(s => (
                      <div
                        key={s}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          s < currentStage || (s === currentStage && stageComplete)
                            ? 'bg-green-500 text-white'
                            : s === currentStage
                            ? 'bg-sky-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {s < currentStage || (s === currentStage && stageComplete) ? '✓' : s}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3">
                {!allComplete && currentStage < 8 && (
                  <Button
                    onClick={goToNextStage}
                    className="flex-1 bg-gradient-to-r from-sky-500 to-indigo-600"
                  >
                    Aşama {currentStage + 1}&apos;e Geç
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Ana Sayfaya Dön
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Main lesson view
  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-4">
        <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
        
        <div className="max-w-lg mx-auto">
          {/* Header with dual progress */}
          <div className="mb-6">
            {/* Stage Progress */}
            <div className="flex items-center justify-between mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
              Modüller
            </Button>
            <div className="text-center">
              <p className="text-sm font-bold text-sky-600">
                Aşama {currentStage}/8
              </p>
              <p className="text-xs text-gray-500">
                {lesson?.stage_name || `Aşama ${currentStage}`}
              </p>
            </div>
            <div className="w-20"></div>
          </div>
          
          {/* Stage indicator dots */}
          <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(s => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  s < currentStage
                    ? 'bg-green-500'
                    : s === currentStage
                    ? 'bg-sky-500 scale-125'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          {/* Question Progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-600">
              Soru {currentQuestion + 1}/{lesson?.exercises?.length || 8}
            </span>
            <span className="text-sm text-gray-500">
              {score} doğru
            </span>
          </div>
          <Progress 
            value={((currentQuestion + 1) / (lesson?.exercises?.length || 8)) * 100} 
            className="h-3"
          />
        </div>

        {/* Exercise Card */}
        <Card className="border-2 border-sky-200 shadow-xl">
          <CardContent className="p-6">
            {renderExercise()}
          </CardContent>
        </Card>
      </div>
    </div>
    </CabinCrewLayout>
  );
}
